// 첨부 한 개. **펼칠 수 있는가는 서버가 정한다** — `preview_slot` 이 없으면 내려받기뿐이다.
// 클라이언트가 MIME 만 보고 `<img>` 를 그리면 허용 목록이 장식이 된다.

import { Download, File, FileText } from 'lucide-react'
import { useState } from 'react'
import { fmt_bytes } from './format.ts'
import { useT } from './i18n.ts'
import { ButtonLink } from './kit/button.tsx'
import { Dialog } from './kit/dialog.tsx'
import { Skeleton } from './kit/feedback.tsx'

type Preview = 'image' | 'video' | 'audio' | 'pdf' | 'none'

export interface FileView {
  attachment: { id: string; filename: string; mime: string; bytes: number }
  preview: Preview
  download_slot: string
  preview_slot?: string | undefined
}

/** id → 받을 곳. `null` 은 "물어봤는데 없다"(지워졌거나 못 본다). */
export type FileMap = ReadonlyMap<string, FileView | null>

export function AttachmentView({
  id,
  handout,
}: {
  id: string
  handout: FileView | null | undefined
}) {
  const t = useT()
  if (handout === undefined) {
    return (
      <div className="Attachment loading" role="status" aria-label={t('파일 불러오는 중')}>
        <Skeleton shape="block" />
      </div>
    )
  }
  if (handout === null) {
    return (
      <div className="Attachment gone">
        <File size={16} aria-hidden />
        {t('파일을 찾을 수 없어요 ({id})', { id: id.slice(0, 8) })}
      </div>
    )
  }
  const { attachment, preview_slot } = handout
  return (
    <div className={`Attachment ${handout.preview}`}>
      {preview_slot !== undefined && <Shown handout={handout} slot={preview_slot} />}
      {/* 파일 칩: 종류 아이콘 · 이름 · 크기 · 받기. 칩 전체가 받기 링크다. */}
      <a className="download" href={handout.download_slot} download={attachment.filename}>
        <span className="kind" aria-hidden>
          {handout.preview === 'pdf' ? <FileText size={18} /> : <File size={18} />}
        </span>
        <span className="name">{attachment.filename}</span>
        <span className="size">({size_text(attachment.bytes)})</span>
        <Download className="get" size={16} aria-hidden />
      </a>
    </div>
  )
}

function Shown({ handout, slot }: { handout: FileView; slot: string }) {
  const t = useT()
  const name = handout.attachment.filename
  switch (handout.preview) {
    case 'image':
      return <Zoom name={name} slot={slot} download={handout.download_slot} />
    case 'video':
      // biome-ignore lint/a11y/useMediaCaption: 올린 파일에 자막 트랙이 없다
      return <video src={slot} controls preload="metadata" />
    case 'audio':
      // biome-ignore lint/a11y/useMediaCaption: 올린 파일에 자막 트랙이 없다
      return <audio src={slot} controls preload="metadata" />
    case 'pdf':
      return (
        <a className="Link open" href={slot} target="_blank" rel="noreferrer noopener">
          <FileText size={14} aria-hidden />
          {t('PDF 열기')}
        </a>
      )
    case 'none':
      return null
  }
}

/**
 * 그림은 누르면 크게 본다(3차 F159). 줄 안의 그림은 360px 로 눌려 있어 스크린숏의 글자를 못 읽는다 —
 * 누를 곳처럼 보이는데 아무 일도 없던 자리다. 창은 Esc·바깥 누르기로 닫히고 아래 줄에 받기가 선다.
 */
function Zoom({ name, slot, download }: { name: string; slot: string; download: string }) {
  const t = useT()
  const [open, set_open] = useState(false)
  return (
    <>
      <button
        type="button"
        className="zoom"
        aria-label={t('{name} 크게 보기', { name })}
        onClick={() => set_open(true)}
      >
        <img src={slot} alt={name} loading="lazy" />
      </button>
      <Dialog
        open={open}
        on_close={() => set_open(false)}
        title={name}
        size="lg"
        className="Lightbox"
        footer={
          <ButtonLink href={download} download={name} icon={<Download size={16} aria-hidden />}>
            {t('다운로드')}
          </ButtonLink>
        }
      >
        <img src={slot} alt={name} />
      </Dialog>
    </>
  )
}

/** 사람이 읽는 크기(`format.ts` 의 `fmt_bytes`). 검색 판도 같은 모양을 쓴다. */
export function size_text(bytes: number): string {
  return fmt_bytes(bytes)
}
